import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { IS_CONFIGURED, AUTH_TELEMETRY_URL } from '@/amplify-config'

/**
 * /login is a thin shim that auto-redirects to Cognito's hosted UI on
 * mount. Three reasons we don't render our own form here:
 *
 *  1. **Bot traffic hits Cognito first.** AWS-hosted login has WAF +
 *     rate-limiting baked in; our React bundle does not. Pushing every
 *     anonymous visit to Cognito means scrapers and credential-stuffing
 *     bots never touch our app code.
 *  2. **One source of truth for auth UX.** Forgot password, MFA,
 *     forced password change, and any future identity providers (Entra
 *     OIDC, Google, etc.) live on the hosted page automatically — no
 *     parallel implementation here to keep in sync.
 *  3. **Identity providers render natively.** Once we register Entra as
 *     an OIDC IdP in Cognito, the hosted UI shows a "Sign in with
 *     Microsoft" button above the email/password form for free. Users
 *     pick their flow on Cognito's page, not ours.
 *
 * The only time this page renders content is:
 *   - Cognito redirected back with ?error_description=… (we surface it)
 *   - The user is already signed in (we bounce them home immediately)
 *   - Mock-mode (no IS_CONFIGURED) — we just route to /
 */
export function LoginPage() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  // If the user just clicked Sign out, show a confirmation screen with an
  // explicit "Sign in again" button instead of auto-redirecting. Without
  // that gate, federated users (Microsoft) get instant re-auth from the
  // still-valid IdP session cookie and Sign out feels broken.
  //
  // We store a TIMESTAMP not a boolean — Cognito's /logout redirect chain
  // can mount /login multiple times in a row (302 → 302 → 302 → /login),
  // and useState's read-and-remove pattern only catches the first mount.
  // A time window (≤30s since signOut) makes the flag resilient: each
  // mount can read it independently and reach the same conclusion. The
  // flag is cleared when the user clicks "Sign in again" or naturally
  // ages out, so a stale flag can't lock the page forever.
  const justSignedOut = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('penser:signed-out-at')
      if (!raw) return false
      const ms = Number(raw)
      if (!Number.isFinite(ms)) return false
      return (Date.now() - ms) < 30_000
    } catch { return false }
  }, [])
  // Was this a 2-hour-idle logout (vs a deliberate Sign out)? Message differs.
  const idleLogout = useMemo(() => {
    try {
      if (sessionStorage.getItem('penser:idle-logout') !== '1') return false
      sessionStorage.removeItem('penser:idle-logout')
      return true
    } catch { return false }
  }, [])

  // Authenticated already (fresh tab on the same domain) — go home.
  useEffect(() => {
    if (!loading && user) navigate('/', { replace: true })
  }, [user, loading, navigate])

  // Cognito redirects here with ?error_description=… on auth failure
  // (account disabled, IdP misconfig, etc.). Translate the few opaque
  // ones into something a human can act on; pass the rest through verbatim
  // so we don't accidentally hide useful detail.
  //
  // Side effect: fire-and-forget the raw error to the auth-telemetry
  // Lambda so federation failures land in CloudWatch. For users who
  // already have a federated identity linked, Cognito doesn't fire any
  // Lambda on subsequent sign-ins — so these redirect errors are the
  // ONLY signal that something went wrong, and without this POST nothing
  // gets logged anywhere AWS-side.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search)
    const rawErr = sp.get('error')
    const rawDesc = sp.get('error_description')
    const raw = rawDesc ?? rawErr
    if (!raw) return
    const decoded = decodeURIComponent(raw.replace(/\+/g, ' '))
    setError(friendlyAuthError(decoded))
    if (AUTH_TELEMETRY_URL) {
      // Fire-and-forget — never block the user's view on telemetry.
      // keepalive lets the request survive the auto-redirect away from
      // /login (when the user clicks "Try again"), since the next
      // navigation would otherwise cancel an in-flight fetch.
      fetch(AUTH_TELEMETRY_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          error: rawErr ? decodeURIComponent(rawErr.replace(/\+/g, ' ')) : '',
          error_description: rawDesc ? decoded : '',
          user_agent: navigator.userAgent,
          page_url: window.location.href.slice(0, 500),
        }),
        keepalive: true,
      }).catch(() => { /* swallow — telemetry must never break the page */ })
    }
  }, [])

  // SSO-only (B51): we no longer auto-redirect to the hosted UI (which shows
  // the password form). Instead the page presents two explicit paths:
  //   • Microsoft — straight to the Entra IdP, skipping the hosted-UI chooser
  //     so the password form is never shown to normal users.
  //   • Admin sign-in — the hosted UI with the email/password form (+ required
  //     MFA), for the few native break-glass admins when Entra is unavailable.
  async function microsoftSignIn() {
    try { sessionStorage.removeItem('penser:signed-out-at') } catch { /* ignored */ }
    if (!IS_CONFIGURED) { navigate('/', { replace: true }); return }
    const { signInWithRedirect } = await import('aws-amplify/auth')
    try {
      await signInWithRedirect({ provider: { custom: 'Microsoft' } })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed')
    }
  }

  async function adminSignIn() {
    try { sessionStorage.removeItem('penser:signed-out-at') } catch { /* ignored */ }
    if (!IS_CONFIGURED) { navigate('/', { replace: true }); return }
    const { signInWithRedirect } = await import('aws-amplify/auth')
    try {
      // No provider → Cognito hosted UI with the email/password form + MFA.
      await signInWithRedirect()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed')
    }
  }

  return (
    <div className="grid min-h-screen supports-[height:100dvh]:min-h-dvh place-items-center bg-gradient-to-br from-slate-50 via-white to-penser-blue/5 p-6">
      <div className="w-full max-w-sm text-center">
        {/* The merged mark — the same one the sidebar and app shell use, so the
                sign-in page and the app agree about whose portal this is. It
                carries all three operating companies rather than Penser alone. */}
            <img src="/merged_logo_1.svg" alt="Penser · Innovative Claim Solutions · Arissa" className="mx-auto mb-4 w-full max-w-[280px]" />
        <div className="mb-6 text-xs uppercase tracking-widest text-slate-400">Business Development Portal</div>

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-left text-xs text-red-700">
            <div className="mb-1 font-semibold">Sign-in didn't complete</div>
            <div className="mb-3 whitespace-pre-wrap">{error}</div>
            <button
              onClick={() => { setError(null); window.location.reload() }}
              className="penser-button-ghost text-xs"
            >
              Try again
            </button>
          </div>
        ) : justSignedOut ? (
          // Post-signout confirmation. No auto-redirect — user has to click
          // to sign in again. Prevents instant federated re-auth from the
          // still-valid Microsoft session cookie that Sign out can't reach.
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-card">
            <div className="mb-1 text-sm font-semibold text-slate-800">
              {idleLogout ? 'Signed out after 2 hours of inactivity' : "You're signed out"}
            </div>
            <div className="mb-4 text-[11px] text-slate-500">
              {idleLogout
                ? 'For security we sign you out after two hours with no activity. Your saved work is safe — sign in again to pick up where you left off.'
                : 'Your Penser session is cleared. Microsoft may still remember you on this device — sign in again to continue.'}
            </div>
            <button
              onClick={() => void microsoftSignIn()}
              className="penser-button-primary w-full justify-center"
            >
              Sign in with Microsoft
            </button>
            <button
              onClick={() => void adminSignIn()}
              className="mt-3 text-[11px] text-slate-400 underline-offset-2 hover:text-slate-600 hover:underline"
            >
              Admin sign-in
            </button>
          </div>
        ) : (
          <div>
            <button
              onClick={() => void microsoftSignIn()}
              className="penser-button-primary w-full justify-center"
            >
              {/* Inline Microsoft logo squares */}
              <span className="mr-1 inline-grid grid-cols-2 gap-px align-middle" aria-hidden>
                <span className="h-2 w-2 bg-[#F25022]" /><span className="h-2 w-2 bg-[#7FBA00]" />
                <span className="h-2 w-2 bg-[#00A4EF]" /><span className="h-2 w-2 bg-[#FFB900]" />
              </span>
              Sign in with Microsoft
            </button>
            <button
              onClick={() => void adminSignIn()}
              className="mt-3 text-[11px] text-slate-400 underline-offset-2 hover:text-slate-600 hover:underline"
            >
              Admin sign-in
            </button>
          </div>
        )}

        <div className="mt-6 text-[11px] text-slate-400">
          Cognito Managed Login · Penser NA · v2
        </div>
      </div>
    </div>
  )
}

/**
 * Translate Cognito's raw error_description into a message a user can
 * actually act on. We only rewrite errors we have a confident
 * interpretation of — anything unknown passes through verbatim so support
 * still has the original string to work with.
 */
function friendlyAuthError(raw: string): string {
  const m = raw.toLowerCase()

  if (m.includes('invalid providername/username combination')) {
    return (
      "We couldn't sign you in through Microsoft this time. " +
      "This usually clears itself if you sign out of Microsoft on this " +
      "device (or open a private window) and try again. If it keeps " +
      "happening, ping admin so they can re-link your account."
    )
  }
  if (m.includes('user account is not authorized')) {
    return "Your account isn't authorized for this portal yet. Ask admin to add you in /admin/users."
  }
  if (m.includes('passwordresetrequired')) {
    return "Your password needs to be reset before you can sign in. Use 'Forgot your password?' on the next screen."
  }
  if (m.includes('user is disabled')) {
    return 'This account is disabled. Ask admin to re-enable it.'
  }
  if (m.startsWith('aadsts9002326')) {
    return (
      "Microsoft rejected the sign-in because the Penser app registration's " +
      "redirect URI is on the wrong platform type. Admin needs to move it to " +
      "'Web' in the Azure portal."
    )
  }
  if (m.startsWith('aadsts50011')) {
    return (
      "Microsoft rejected the sign-in because Cognito's callback URL isn't " +
      "registered on the Entra app. Admin needs to add it under Authentication " +
      "→ Web platform."
    )
  }
  if (m.startsWith('aadsts')) {
    // Pass the AADSTS code + first line through so support can act on it.
    return `Microsoft rejected the sign-in. ${raw}`
  }
  return raw
}
